import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import type { BoundingBox } from "@b2bportal/core-types";
import { useMapStyles } from "@tenant/ui";
import { MapMarker as DefaultMapMarker } from "@tenant/ui";
import type {
  Map as MapType,
  LatLngBounds,
  onGoogleApiLoadedProps,
} from "google-maps-react-markers";
import type Supercluster from "supercluster";
import useSupercluster from "use-supercluster";
import type { MapComponentProps, MapPointer } from "../Map";
import {
  type ClusterMarkerProps,
  type MapMarkerComponentProps,
  MapMarkerType,
  type SingleMarkerProps,
} from "../types";

export const useMap = <T>(props: MapComponentProps<T>) => {
  const {
    defaultCentroid = { lat: 0, lng: 0 },
    defaultZoom = 13,
    centroid,
    searchBoundingBox,
    mapMode = "Centroid",
    zoom,
    onMapChange,
    zoomControl = true,
    onMapReady,
    points,
    highlightedId,
    MapMarker,
    clusterLabel = (count) => `${count}`,
    useClusters = true,
    gestureHandling = "auto",
    mapOptions,
  } = props;
  const styles = useMapStyles();

  const mapRef = useRef<MapType | null>(null);
  const [mapReady, setMapReady] = useState(false);

  const [internalCentroid, setInternalCentroid] = useState(
    centroid ?? defaultCentroid
  );
  const [internalZoom, setInternalZoom] = useState(zoom ?? defaultZoom);

  const isControlledCentroid = centroid != null;

  useEffect(() => {
    if (centroid != null) {
      if (
        internalCentroid.lat !== centroid.lat &&
        internalCentroid.lng !== centroid.lng
      ) {
        setInternalCentroid(centroid);
        moveMapTo(centroid.lat, centroid.lng);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centroid?.lat, centroid?.lng]);

  useEffect(() => {
    if (zoom != null) {
      if (internalZoom !== zoom) {
        setInternalZoom(zoom);
        setMapZoom(zoom);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom]);

  const [bounds, setBounds] = useState<BoundingBox>({
    southWest: { lat: 0, lng: 0 },
    northEast: { lat: 0, lng: 0 },
  });

  const [selectedMarkerId, setSelectedMarkerId] = useState<string | undefined>(
    undefined
  );

  const onChange = useCallback(
    ({
      zoom,
      bounds,
      center,
    }: {
      bounds: LatLngBounds;
      center: (number | undefined)[];
      zoom: number;
    }) => {
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const newBounds = {
        southWest: {
          lng: sw.lng(),
          lat: sw.lat(),
        },
        northEast: {
          lng: ne.lng(),
          lat: ne.lat(),
        },
      };
      setBounds(newBounds);

      setInternalZoom(zoom);
      if (center[1] != null && center[0] != null) {
        onMapChange?.({
          zoom,
          centroid: { lat: center[1], lng: center[0] },
          boundingBox: newBounds,
        });
      }
    },

    [onMapChange]
  );

  const toSuperClusterPoints = (mapPoints: Array<MapPointer<T>>) => {
    return mapPoints.map((point, index) => ({
      type: "Feature" as const,
      properties: {
        data: point.data,
        label: point.label,
        pointerId: point.id,
        sortIndex: index,
      },
      geometry: {
        type: "Point" as const,
        coordinates: [point.coordinates.lng, point.coordinates.lat],
      },
    }));
  };

  const clusterOptions = useClusters
    ? {
        radius: 150,
        maxZoom: 16,
      }
    : {
        radius: 0,
        minZoom: 17,
      };
  const { clusters, supercluster } = useSupercluster({
    points: toSuperClusterPoints(points),
    zoom: internalZoom,
    bounds: [
      bounds.southWest.lng,
      bounds.southWest.lat,
      bounds.northEast.lng,
      bounds.northEast.lat,
    ],
    options: clusterOptions,
  });

  const expansionZoom = useCallback(
    (clusterId: number) => {
      try {
        return supercluster?.getClusterExpansionZoom(clusterId);
      } catch (e) {
        return internalZoom;
      }
    },
    [supercluster]
  );

  const setMapZoom = (zoom: number) => {
    mapRef?.current?.setZoom(zoom);
  };

  const moveMapToBounds = useCallback((boundingBox: BoundingBox) => {
    // @ts-ignore "error TS2339: Property 'maps' does not exist on type" may be raised in some tenants that uses other Google APIs.
    const bounds = new window.google.maps.LatLngBounds(
      {
        lat: boundingBox.southWest.lat,
        lng: boundingBox.southWest.lng,
      },
      {
        lat: boundingBox.northEast.lat,
        lng: boundingBox.northEast.lng,
      }
    );
    mapRef?.current?.fitBounds(bounds);
  }, []);

  const moveMapTo = useCallback((lat: number, lng: number, zoom?: number) => {
    if (zoom != null) {
      mapRef?.current?.setZoom(zoom);
    }
    // Moves the center of the map to lat, lng
    mapRef?.current?.panTo({
      lat: lat,
      lng: lng,
    });
  }, []);

  const handleAPILoaded = useCallback(
    ({ map }: onGoogleApiLoadedProps) => {
      mapRef.current = map;

      // @ts-ignore: map.zoom does not exist on latest types, but works on google map markers libs
      const loadedZoom = map.getZoom != null ? map.getZoom() : map.zoom;
      // A map re-created in a hidden container (mobile list, back navigation)
      // has no zoom until its first idle. Keep the current zoom rather than
      // handing NaN to the clustering, which throws and unmounts the page.
      if (loadedZoom != null) {
        setInternalZoom(loadedZoom);
      }
      if (mapMode === "BoundingBox" && searchBoundingBox != null) {
        moveMapToBounds(searchBoundingBox);
      } else if (isControlledCentroid) {
        moveMapTo(centroid.lat, centroid.lng);
      } else {
        moveMapTo(defaultCentroid.lat, defaultCentroid.lng);
      }

      setMapReady(true);

      // @ts-ignore: map ref is being checked to see if its null
      if (mapRef.current != null) onMapReady?.(mapRef);
    },
    [
      centroid,
      defaultCentroid,
      isControlledCentroid,
      mapMode,
      moveMapTo,
      moveMapToBounds,
      onMapReady,
      searchBoundingBox,
    ]
  );

  const [mapPoints, setMapPoints] = useState<Array<MapMarkerComponentProps<T>>>(
    []
  );
  const [isLoadingMapPoints, startTransition] = useTransition();

  useEffect(() => {
    startTransition(() => {
      if (clusters == null || clusters.length === 0) setMapPoints([]);

      const hasDataInCluster = (
        dataId: string,
        leaves: Supercluster.PointFeature<any>[]
      ) =>
        highlightedId != null &&
        leaves.some(({ properties: { pointerId } }) => pointerId === dataId);

      const points = clusters.map((cluster) => {
        const [longitude, latitude] = cluster.geometry.coordinates;

        const baseProps = {
          mapRef,
          lat: latitude,
          lng: longitude,
        };

        if ("cluster" in cluster.properties && cluster.properties.cluster) {
          const clusterId = Number(cluster.id);

          const getHighlighted = () => {
            try {
              const clusterLeaves =
                supercluster?.getLeaves(clusterId, Infinity) || [];
              return highlightedId != null
                ? hasDataInCluster(highlightedId, clusterLeaves)
                : false;
            } catch {
              return false;
            }
          };

          return {
            ...baseProps,
            markerType: MapMarkerType.CLUSTER,
            isHighlighted: getHighlighted(),
            onClick: () => {
              setSelectedMarkerId(undefined);
              moveMapTo(latitude, longitude, expansionZoom(clusterId));
            },
            label: clusterLabel(cluster.properties.point_count),
            cluster,
            markerId: `marker-${latitude}-${longitude}-${cluster.properties.cluster_id}`,
          } as ClusterMarkerProps<T>;
        }

        const isHighlighted =
          highlightedId != null &&
          cluster.properties.pointerId === highlightedId;

        const isSelected =
          selectedMarkerId != null &&
          cluster.properties.pointerId === selectedMarkerId;

        return {
          ...baseProps,
          markerType: MapMarkerType.SINGLE,
          isHighlighted,
          isSelected,
          onClick: () => {
            setSelectedMarkerId(cluster.properties.pointerId);
            moveMapTo(latitude, longitude);
          },
          label: cluster.properties.label,
          markerId: `marker-${latitude}-${longitude}-${cluster.properties.pointerId}`,
          cluster,
          sortIndex: cluster.properties.sortIndex,
          onClose: () => {
            setSelectedMarkerId(undefined);
          },
        } as SingleMarkerProps<T>;
      });

      if (useClusters === false) {
        const { markerLimit } = props;
        const sortedPoints =
          markerLimit != null
            ? points
                .sort((a, b) => {
                  if (
                    a.markerType === MapMarkerType.CLUSTER ||
                    b.markerType === MapMarkerType.CLUSTER
                  )
                    return 0;
                  return a.sortIndex - b.sortIndex;
                })
                .slice(0, markerLimit)
            : points;
        setMapPoints(sortedPoints);
      } else {
        setMapPoints(points);
      }
    });
  }, [
    clusters,
    useClusters,
    highlightedId,
    selectedMarkerId,
    clusterLabel,
    supercluster,
    moveMapTo,
    expansionZoom,
    props,
  ]);

  return {
    context: {
      mapZoom: internalZoom,
      styles,
      mapCenter: internalCentroid,
      zoomControl,
      mapRef,
      mapPoints,
      MapMarker: MapMarker ? MapMarker : DefaultMapMarker,
      gestureHandling,
      mapOptions,
    },
    handlers: {
      handleAPILoaded,
      onChange,
    },
  };
};
